import {t} from "@lingui/core/macro";
import * as React from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {PageLifecycle} from "@khan/analytics-performance";
import type {Referral, Role, Step} from "@khan/auth-shared";
import {PermissionsInfo} from "@khan/shared-core";
import {
    isKhanAcademyUrl,
    parseUrl,
    preserveMarketingParams,
    useLocation,
    type Location,
} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import {
    isKhanmigoReferral,
    isKhanmigoSignupLearnerParentSignup,
    isKhanmigoSignupTeacherSignup,
} from "#auth/components/util.ts";
import {KhanmigoHeader} from "#khanmigo-package/khanmigo-header.tsx";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";
import ProgressShellFull from "#shared/auth/progress-shell/progress-shell-full.tsx";

import SignupDetailScreen from "./signup-detail-screen.tsx";
import SignupIdentifyActionScreen from "./signup-identify-action-screen.tsx";
import SignupIntroActionScreen from "./signup-intro-action-screen.tsx";

type OwnProps = Record<any, any>;

type Props = OwnProps;

export function getReferralType(location: Location): Referral {
    const {
        queryParams: {referral},
    } = location;

    // Allow-list for referral query param
    const referralAllowed = {
        LearnStorm: true,
        KhanmigoCheckout: true,
        KhanmigoSignup: true,
        parent_approval_success: true,
        parent_approval_failed: true,
        DistrictTrials: true,
    }[referral];

    if (referralAllowed) {
        return referral as Referral;
    }
    return "default";
}

export default function SignupPage(props: Props) {
    const location = useLocation();
    const queryParams = location.queryParams;
    const initialRole = getInitialRole(location);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const [role, setRole] = React.useState(initialRole || "learner");
    const [birthdate, setBirthdate] = React.useState("");
    // To prove they are not under 13, the user needs to self-identify as
    // a teacher, parent, or enter a birthdate that shows they are over
    // 13. When navigating straight to the email form via a marketing
    // page aimed at teachers or parents, we know the user is not under 13.
    const [under13, setUnder13] = React.useState(
        initialRole !== "teacher" && initialRole !== "parent",
    );

    useOnMountEffect(() => {
        PageLifecycle.reportPageLifecycleTiming("signup_page", {
            interactive: true,
            usable: true,
        });

        publishAnalyticsEvent(
            {
                eventType: "SignupPageViewed",
                eventSchemaVersion: 1,
            },
            "pageview_signup", // legacy bingo conversion id
        );
    });

    const handleRoleChange = (role: Role) => setRole(role);
    const handleBirthdateChange = (birthdate: string, under13: boolean) => {
        setBirthdate(birthdate);
        setUnder13(under13);
    };

    let continueUrl = isKhanAcademyUrl(queryParams.continue || "")
        ? queryParams.continue
        : "/";

    const parsedContinueUrl = parseUrl(continueUrl);

    // This isn't the greatest because we potentially ignore the continue param
    // but in the interest of time, we're going to always send learners
    // and parents to the checkout page because it's only free for teachers
    // NOTE: we only do this on signup, and not login because the user
    // actually selects a role on this page.
    if (
        isKhanmigoSignupLearnerParentSignup(role, getReferralType(location)) &&
        parsedContinueUrl.pathname !== "/khanmigo/checkout"
    ) {
        continueUrl = "/khanmigo/checkout";
    } else if (
        isKhanmigoSignupTeacherSignup(role, getReferralType(location)) &&
        // It's important not to rewrite the continueUrl if the user
        // was already headed for Teacher Tools enrollment as query
        // parameters like the user's referral code may otherwise be
        // lost.
        parsedContinueUrl.pathname !== "/khanmigo/teacher-tools"
    ) {
        continueUrl = "/khanmigo/teacher-tools";
    }

    if (PermissionsInfo.isLoggedIn) {
        return (
            <SmartRedirect
                to={preserveMarketingParams(
                    continueUrl,
                    new URLSearchParams(location.search),
                )}
            />
        );
    }

    const referral = getReferralType(location);

    const actionScreen = (
        step: Step,
        updateStep: JSX.LibraryManagedAttributes<
            typeof SignupIdentifyActionScreen,
            React.ComponentProps<typeof SignupIdentifyActionScreen>
        >["updateStep"],
    ): null | React.ReactElement<any> => {
        return (
            <>
                {step === "intro" ? (
                    <SignupIntroActionScreen
                        continueUrl={continueUrl}
                        initialRole={role}
                        birthdate={birthdate}
                        referral={getReferralType(location)}
                        onRoleChange={handleRoleChange}
                        onBirthdateChange={handleBirthdateChange}
                        updateStep={updateStep}
                    />
                ) : (
                    <SignupIdentifyActionScreen
                        continueUrl={continueUrl}
                        role={role}
                        birthdate={birthdate}
                        under13={under13}
                        referral={getReferralType(location)}
                        updateStep={updateStep}
                    />
                )}
            </>
        );
    };

    const initialStep =
        queryParams.email && (role === "teacher" || role === "parent")
            ? // TODO(davemcalpine): this probably should be "identify" to
              // match the existing step name, but it's been this way so
              // long, I'd rather investigate more before changing it.
              "identity"
            : "intro";

    return (
        <PageRoot
            settings={{
                title: t`Sign up`,
                // If the user is coming from Khanmigo, we don't want
                // to show the standard header, we'll instead show a
                // custom one
                header: !isKhanmigoReferral(referral),
            }}
        >
            {isKhanmigoReferral(referral) && <KhanmigoHeader />}
            <ProgressShellFull
                referral={referral}
                initialStep={initialStep}
                detailScreen={() => (
                    <SignupDetailScreen role={role} referral={referral} />
                )}
                actionScreen={actionScreen}
            />
        </PageRoot>
    );
}

const getInitialRole = (location: Location): Role => {
    const {queryParams} = location;
    if (queryParams.isteacher) {
        return "teacher";
    }
    if (queryParams.isparent || queryParams.parent) {
        return "parent";
    }
    // default to learner
    return "learner";
};
